/**
 * Pure shot evaluator: interprets a physics event log against a variant rule and
 * returns whether the shot scored / fouled. No globals, no rendering — fully
 * unit-testable with synthetic logs.
 */
import type { BallId, GameEvent } from '../types';
import type { CaromRuleConfig, ShotEvaluation } from './types';

interface DerivedSequence {
  firstObjectBall: BallId | null;
  tFirst: number | null;
  secondObjectBall: BallId | null;
  tSecond: number | null;
  /** Every distinct ball the cue ball contacted. */
  contacted: Set<BallId>;
  /** Times of cue-ball cushion contacts, ascending. */
  cushionTimes: number[];
  anyOffTable: boolean;
}

/** Walk the (time-ordered) log and extract the cue ball's contact sequence. */
export function deriveSequence(events: GameEvent[], striker: BallId): DerivedSequence {
  const sorted = [...events].sort((a, b) => a.t - b.t);

  let firstObjectBall: BallId | null = null;
  let tFirst: number | null = null;
  let secondObjectBall: BallId | null = null;
  let tSecond: number | null = null;
  const contacted = new Set<BallId>();
  const cushionTimes: number[] = [];
  let anyOffTable = false;

  for (const e of sorted) {
    switch (e.kind) {
      case 'ball-ball': {
        const partner = e.a === striker ? e.b : e.b === striker ? e.a : null;
        if (partner === null) break; // collision between the two object balls — irrelevant
        contacted.add(partner);
        if (firstObjectBall === null) {
          firstObjectBall = partner;
          tFirst = e.t;
        } else if (partner !== firstObjectBall && secondObjectBall === null) {
          secondObjectBall = partner;
          tSecond = e.t;
        }
        break;
      }
      case 'ball-cushion':
        if (e.ball === striker) cushionTimes.push(e.t);
        break;
      case 'ball-offtable':
        anyOffTable = true;
        break;
      default:
        break;
    }
  }

  return { firstObjectBall, tFirst, secondObjectBall, tSecond, contacted, cushionTimes, anyOffTable };
}

function countBefore(times: number[], t: number | null): number {
  if (t === null) return times.length;
  return times.filter((x) => x < t).length;
}

function countBetween(times: number[], lo: number | null, hi: number | null): number {
  if (lo === null || hi === null) return 0;
  return times.filter((x) => x > lo && x < hi).length;
}

function buildReason(
  rule: CaromRuleConfig,
  scored: boolean,
  bothContacted: boolean,
  hitAllRequired: boolean,
  firstObjectBall: BallId | null,
  cushionCount: number
): string {
  if (scored) {
    return rule.minCushions > 0
      ? `Carom! ${cushionCount} cushion${cushionCount === 1 ? '' : 's'}`
      : 'Carom!';
  }
  if (firstObjectBall === null) return 'Missed — cue ball hit nothing';
  if (rule.caromObjectBalls && !hitAllRequired) return 'Missed — must carom off both reds';
  if (!bothContacted) return 'Missed the second ball';
  return `Only ${cushionCount} of ${rule.minCushions} cushion${rule.minCushions === 1 ? '' : 's'}`;
}

/**
 * Evaluate one shot.
 * @param events    the shot's contact log (any order; sorted internally by time)
 * @param striker   the active player's cue ball
 * @param rule      the variant rule config
 * @param isBreakShot whether this is the opening break (enables break-first-hit foul)
 */
export function evaluateEventLog(
  events: GameEvent[],
  striker: BallId,
  rule: CaromRuleConfig,
  isBreakShot: boolean
): ShotEvaluation {
  const seq = deriveSequence(events, striker);
  const bothContacted = seq.firstObjectBall !== null && seq.secondObjectBall !== null;
  const cushionsBeforeSecond = countBefore(seq.cushionTimes, seq.tSecond);
  const cushionsBetween = countBetween(seq.cushionTimes, seq.tFirst, seq.tSecond);
  // cushions the cue ball banked before touching ANY object ball (빈쿠션 먼저).
  // When the cue never contacts a ball, tFirst is null and this counts them all.
  const cushionsBeforeFirst = countBefore(seq.cushionTimes, seq.tFirst);

  const diagnostics = {
    firstObjectBall: seq.firstObjectBall,
    secondObjectBall: seq.secondObjectBall,
    cushionsBeforeSecond,
    cushionsBetween,
    cushionsBeforeFirst,
    bothContacted,
  };

  // --- Fouls take precedence over any geometric carom ---
  if (rule.foulOnOffTable && seq.anyOffTable) {
    return { scored: false, points: 0, foul: true, endTurn: true, reason: 'Foul: a ball left the table', ...diagnostics };
  }
  if (isBreakShot && rule.breakRequiresFirstHit && seq.firstObjectBall !== rule.breakRequiresFirstHit) {
    return {
      scored: false, points: 0, foul: true, endTurn: true,
      reason: `Foul: must strike the ${rule.breakRequiresFirstHit} ball first on the break`,
      ...diagnostics,
    };
  }
  if (rule.foulOnNoContact && seq.firstObjectBall === null) {
    return { scored: false, points: 0, foul: true, endTurn: true, reason: 'Foul: cue ball hit nothing', ...diagnostics };
  }
  // Four-ball: touching the opponent's cue ball (anything not a scoring object
  // ball) is a foul even if both reds were also struck.
  if (rule.foulOnOpponentBall && rule.caromObjectBalls) {
    const hitOpponent = [...seq.contacted].some((b) => !rule.caromObjectBalls!.includes(b));
    if (hitOpponent) {
      return { scored: false, points: 0, foul: true, endTurn: true, reason: 'Foul: hit the opponent ball', ...diagnostics };
    }
  }

  // --- Scoring ---
  const cushionCount = rule.cushionWindow === 'between' ? cushionsBetween : cushionsBeforeSecond;
  // Four-ball requires caroming off every listed object ball; otherwise any two distinct balls.
  const hitAllRequired = rule.caromObjectBalls ? rule.caromObjectBalls.every((b) => seq.contacted.has(b)) : bothContacted;
  const scored = hitAllRequired && cushionCount >= rule.minCushions;

  return {
    scored,
    points: scored ? rule.pointsPerCarom : 0,
    foul: false,
    endTurn: !scored,
    reason: buildReason(rule, scored, bothContacted, hitAllRequired, seq.firstObjectBall, cushionCount),
    ...diagnostics,
  };
}
